// SEQ-199: the grid widget's whole data model, as a pure function with no
// React and no CSS import, so a deno test can prove it (the component file
// pulls in a stylesheet deno cannot resolve; see monthOnMonthRenderModel.ts).
//
// WHAT IT BUILDS. One row per project, a column per month for each chosen
// measure, then the summary columns the transformation library produces
// (Total, % of Total, Cumulative %, Rank, Category), an averages row on top and
// conditional formatting bands. Nothing here knows the word "Pareto" as more
// than a label: the cut-offs, labels and bands all come from the config.
//
// WHERE THE FIGURES COME FROM, AND WHAT IS NEVER DONE WITH THEM
// - Month cells are read straight from the `project_month` rows (raw wages).
// - Total cells are the project-grain rollup the Totals view shows
//   (multiplied wages). Total is NEVER the sum of the month cells for cost or
//   profit, for the reason projectMonthTable.ts sets out, and the grid says so
//   in its notes.
// - A project with no row for a month has a BLANK cell, never $0.
// - Derived columns (% of Total, Cumulative %, Rank, Category) are computed
//   on the rows in descending order of the summary measure's Total, whatever
//   order the viewer sorted the display by, because a cumulative percent only
//   means something down a ranked list.
//
// SAFE MODE (Josh, SEQ-199). Every currency text goes through
// formatMetricValue, which masks money while Safe Mode is on, so a cell, a
// total, an average and every string built from them is already masked.
// Percentages, ranks, cumulative percentages and categories stay. Two further
// rules close the leaks a mask alone does not: conditional formatting on a
// money measure is switched off under Safe Mode (a colour band on dollars is a
// size hint), and the model hands the component display text only, never a
// number, for anything that reaches the DOM. Exports are disabled by the
// component, as everywhere else on this page.

import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import { isSafeModeActive } from "../../lib/calculations";
import { RAW_WAGES_NOTE } from "../../lib/pnl/wagesMultiplierGrain";
import {
  gridMeasure,
  type GridMeasureDef,
  type GridSummaryColumn,
  type PnlGridSort,
  type ResolvedGridConfig,
} from "../../lib/pnl/pnlGridConfig";
import { metricsToRollup, type PnlRollup } from "./rollup";
import { formatMonthLabel } from "./pnlAnalyticsFormat";
import { PARTIAL_MONTH_SUFFIX } from "./monthOnMonthRenderModel";
import { COSTS_NOT_ENTERED_LABEL } from "./projectMonthCosts";
import {
  average,
  bandMarker,
  classifyBand,
  cumulativePercent,
  describeBands,
  normaliseParetoConfig,
  paretoClassify,
  percentOfTotal,
  rank,
  topNWithOther,
  OTHER_ROW_ID,
  type BandColour,
  type FormatBand,
  type TransformRow,
} from "./transforms/index.ts";

export const OTHER_ROW_LABEL = "Other";
export const AVERAGE_ROW_LABEL = "Average";
export const NAME_COLUMN_ID = "name";

export type GridColumnKind = "name" | "month" | "total" | "derived";

export interface GridColumn {
  id: string;
  kind: GridColumnKind;
  /** Header line above the label: the month ("Jul 2026"), "Total", or the summary measure's name. Empty for the name column. */
  group: string;
  label: string;
  unit: PnlUnit | "text";
  align: "left" | "right";
  /** The measure the column shows, when it shows one. */
  measure?: string;
  hidden: boolean;
  /** For a month column: its 'YYYY-MM-01', which the costs-entered rule (SEQ-232) is keyed on. */
  month?: string;
}

export interface GridCell {
  /** What the viewer reads. Already masked for money under Safe Mode. */
  text: string;
  /** The conditional formatting band this cell falls in, when a rule applies. Never set on a masked money cell. */
  band?: { colour: BandColour; label: string; marker: string };
  /** SEQ-232: "No costs entered", on the first measure of a project-month that has income and no costs yet. */
  suffix?: string;
}

export interface GridRow {
  id: string;
  label: string;
  /** The project status, shown in the row's title text. Empty for Other. */
  status: string;
  isOther: boolean;
  cells: Record<string, GridCell>;
}

export interface GridLegendEntry {
  measureLabel: string;
  bands: { label: string; range: string; colour: BandColour; marker: string }[];
}

export interface GridModel {
  /** Every column, hidden ones included, so the show/hide control can list them. */
  columns: GridColumn[];
  visibleColumns: GridColumn[];
  rows: GridRow[];
  /** The averages row, or null when the config turns it off. */
  averages: GridRow | null;
  legend: GridLegendEntry[];
  notes: string[];
  /** How many projects the "Other" row stands for. 0 means there is none. */
  foldedCount: number;
  /** The sort actually applied. */
  sort: PnlGridSort;
}

export interface GridProjectInput {
  projectId: string;
  projectName: string;
  status: string;
  /** The project-grain rollup the Totals view shows. Null when the project has none in range. */
  total: PnlRollup | null;
}

export interface GridModelInput {
  /** The month columns, 'YYYY-MM-01', ascending. */
  months: string[];
  /** The `project_month` response rows. Rows for projects not in `projects` are ignored. */
  monthRows: PnlAnalyticsRow[];
  projects: GridProjectInput[];
  config: ResolvedGridConfig;
  /** The viewer's chosen sort. Falls back to the config's saved sort. */
  sort?: PnlGridSort;
  /** The viewer's hidden columns. Falls back to the config's saved list. */
  hiddenColumns?: readonly string[];
  /** Months that are not complete yet (month to date), whose group header says so. */
  partialMonths?: readonly string[];
  /**
   * SEQ-232, the same rule as the Project performance By month table
   * (projectMonthCosts.ts): per project, the months that have income and no
   * costs entered yet. Computed by the caller with the table's own helpers.
   */
  noCostMonths?: ReadonlyMap<string, ReadonlySet<string>>;
  /** The "Show months without costs" switch. Off (the default) leaves flagged project-months out of the figures. */
  showNoCostMonths?: boolean;
}

const MONTH_COLUMN = (month: string, measure: string) => `m:${month}:${measure}`;
const TOTAL_COLUMN = (measure: string) => `s:total:${measure}`;
const DERIVED_COLUMN = (summary: GridSummaryColumn) => `s:${summary}`;

function rollupValue(rollup: PnlRollup | null, measure: string): number | null {
  if (!rollup) return null;
  const value = (rollup as unknown as Record<string, number | null>)[measure];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Decimals the grid shows: whole dollars (the spreadsheet's width), one decimal for percentages. */
function formatGridValue(value: number | null, unit: PnlUnit): string {
  // No data is blank, never "N/A" and never zero, matching the rule in the file header and the footnote.
  if (value === null) return "";
  if (unit === "currency" || unit === "currency_per_hour") return formatMetricValue(value, unit, 0);
  if (unit === "percent") return formatMetricValue(value, unit, 1);
  return formatMetricValue(value, unit);
}

/** Every column the config asks for, in display order, before the viewer hides any. */
export function buildGridColumns(
  months: string[],
  config: ResolvedGridConfig,
  hidden: ReadonlySet<string>,
  partialMonths: ReadonlySet<string> = new Set(),
): GridColumn[] {
  const columns: GridColumn[] = [
    { id: NAME_COLUMN_ID, kind: "name", group: "", label: "Project", unit: "text", align: "left", hidden: false },
  ];
  for (const month of months) {
    for (const key of config.measures) {
      const def = gridMeasure(key);
      if (!def) continue;
      const id = MONTH_COLUMN(month, key);
      const group = `${formatMonthLabel(month)}${partialMonths.has(month) ? PARTIAL_MONTH_SUFFIX : ""}`;
      columns.push({ id, kind: "month", group, label: def.label, unit: def.unit, align: "right", measure: key, month, hidden: hidden.has(id) });
    }
  }
  const summaryDef = gridMeasure(config.summaryMeasure);
  for (const summary of config.summary) {
    if (summary === "total") {
      for (const key of config.measures) {
        const def = gridMeasure(key);
        if (!def) continue;
        const id = TOTAL_COLUMN(key);
        columns.push({ id, kind: "total", group: "Total", label: def.label, unit: def.unit, align: "right", measure: key, hidden: hidden.has(id) });
      }
      continue;
    }
    const id = DERIVED_COLUMN(summary);
    const group = summaryDef?.label ?? "";
    const meta: Record<Exclude<GridSummaryColumn, "total">, { label: string; unit: PnlUnit | "text"; align: "left" | "right" }> = {
      percentOfTotal: { label: "% of Total", unit: "percent", align: "right" },
      cumulativePercent: { label: "Cumulative %", unit: "percent", align: "right" },
      rank: { label: "Rank", unit: "count", align: "right" },
      category: { label: "Category", unit: "text", align: "left" },
    };
    const m = meta[summary];
    columns.push({ id, kind: "derived", group, label: m.label, unit: m.unit, align: m.align, measure: config.summaryMeasure, hidden: hidden.has(id) });
  }
  return columns;
}

interface RawRow {
  id: string;
  label: string;
  status: string;
  isOther: boolean;
  /** Numbers by column id, null for blank. Category lives in `texts`. */
  values: Record<string, number | null>;
  texts: Record<string, string | null>;
  /** Months flagged "No costs entered" for this project (SEQ-232). */
  noCost: ReadonlySet<string>;
}

interface ComputedGrid {
  model: GridModel;
  /** The numbers behind each displayed row (and the averages row), by column id. Private to this module and the export path: it never reaches the component. */
  numbers: Map<string, Record<string, number | null>>;
}

/** The display model. Text only: the component cannot reach a raw figure through it. */
export function buildGridModel(input: GridModelInput): GridModel {
  return computeGrid(input).model;
}

function computeGrid(input: GridModelInput): ComputedGrid {
  const { months, monthRows, projects, config } = input;
  const sort = input.sort ?? config.sort;
  const hidden = new Set(input.hiddenColumns ?? config.hiddenColumns);
  const columns = buildGridColumns(months, config, hidden, new Set(input.partialMonths ?? []));
  const safeMode = isSafeModeActive();

  // 1. Per-project numbers, by column id.
  const byProject = new Map<string, Map<string, PnlRollup>>();
  for (const row of monthRows) {
    if (!row.projectId || !row.periodMonth) continue;
    let perMonth = byProject.get(row.projectId);
    if (!perMonth) {
      perMonth = new Map();
      byProject.set(row.projectId, perMonth);
    }
    perMonth.set(row.periodMonth, metricsToRollup(row.metrics));
  }

  const base: RawRow[] = projects.map((p) => {
    const values: Record<string, number | null> = {};
    const perMonth = byProject.get(p.projectId);
    const noCost = input.noCostMonths?.get(p.projectId) ?? new Set<string>();
    for (const month of months) {
      // SEQ-232: a project-month with income and no costs yet is left out of the figures unless the switch is on.
      const leftOut = noCost.has(month) && input.showNoCostMonths !== true;
      for (const key of config.measures) values[MONTH_COLUMN(month, key)] = leftOut ? null : rollupValue(perMonth?.get(month) ?? null, key);
    }
    // Totals for every chosen measure, and always for the summary measure, which the ranking needs even when its Total column is not shown.
    for (const key of new Set([...config.measures, config.summaryMeasure])) values[TOTAL_COLUMN(key)] = rollupValue(p.total, key);
    return { id: p.projectId, label: p.projectName, status: p.status, isOther: false, values, texts: {}, noCost };
  });

  // 2. Canonical order for the Pareto columns: summary measure's Total, highest first, name breaks ties.
  const summaryTotalCol = TOTAL_COLUMN(config.summaryMeasure);
  const canonical = [...base].sort((a, b) => {
    const av = a.values[summaryTotalCol];
    const bv = b.values[summaryTotalCol];
    if (av === null && bv === null) return a.label.localeCompare(b.label);
    if (av === null) return 1;
    if (bv === null) return -1;
    return bv - av || a.label.localeCompare(b.label);
  });

  // 3. Top N: fold the tail into one Other row. Only dollar and hour columns add up.
  const additiveColumns = [
    ...new Set([
      summaryTotalCol,
      ...columns.filter((c) => (c.kind === "month" || c.kind === "total") && !gridMeasure(c.measure ?? "")?.isPercent).map((c) => c.id),
    ]),
  ];
  let foldedCount = 0;
  let working: RawRow[] = canonical;
  if (config.topN !== null) {
    const folded = topNWithOther(
      canonical.map((r) => ({ id: r.id, values: r.values })),
      config.topN,
      additiveColumns,
    );
    foldedCount = folded.foldedCount;
    if (foldedCount > 0) {
      const kept = canonical.slice(0, folded.rows.length - 1);
      const otherValues = { ...(folded.rows[folded.rows.length - 1].values) } as Record<string, number | null>;
      for (const id of Object.keys(canonical[0].values)) if (!(id in otherValues)) otherValues[id] = null;
      working = [...kept, { id: OTHER_ROW_ID, label: OTHER_ROW_LABEL, status: "", isOther: true, values: otherValues, texts: {}, noCost: new Set() }];
    }
  }

  // 4. Derived columns on the ranked rows.
  const tRows: TransformRow[] = working.map((r) => ({ id: r.id, values: { v: r.values[summaryTotalCol] ?? null } }));
  const pct = percentOfTotal(tRows, "v");
  const cum = cumulativePercent(tRows, "v");
  // Other is a tail of many projects, not one project, so it takes no rank and does not displace a real one.
  const rnk = rank(tRows.filter((t) => t.id !== OTHER_ROW_ID), "v");
  const paretoConfig = normaliseParetoConfig({ bands: config.paretoBands, restLabel: config.paretoRestLabel });
  const pareto = paretoClassify(tRows, "v", paretoConfig);
  for (const r of working) {
    r.values[DERIVED_COLUMN("percentOfTotal")] = pct.get(r.id) ?? null;
    r.values[DERIVED_COLUMN("cumulativePercent")] = cum.get(r.id) ?? null;
    r.values[DERIVED_COLUMN("rank")] = rnk.get(r.id) ?? null;
    r.texts[DERIVED_COLUMN("category")] = pareto.get(r.id) ?? null;
  }

  // 5. The viewer's sort. Other always stays last.
  const sorted = sortRows(working, sort, columns);

  // 6. Display cells with bands.
  const rulesByMeasure = new Map(config.formatRules.map((rule) => [rule.measure, rule.bands]));
  const toCells = (r: RawRow): Record<string, GridCell> => {
    const cells: Record<string, GridCell> = {};
    for (const col of columns) {
      if (col.id === NAME_COLUMN_ID) continue;
      if (col.unit === "text") {
        cells[col.id] = { text: r.texts[col.id] ?? "" };
        continue;
      }
      const value = r.values[col.id] ?? null;
      const def = col.measure ? gridMeasure(col.measure) : undefined;
      const blankForOther = r.isOther && def?.isPercent && col.kind !== "derived";
      const text = blankForOther ? "" : formatGridValue(value, col.unit);
      const cell: GridCell = { text };
      if (col.kind === "month" && col.month !== undefined && col.measure === config.measures[0] && r.noCost.has(col.month)) cell.suffix = COSTS_NOT_ENTERED_LABEL;
      const bands = col.kind === "month" || col.kind === "total" ? rulesByMeasure.get(col.measure ?? "") : undefined;
      const suppressed = safeMode && def?.money === true;
      if (bands && !suppressed && !blankForOther) {
        const band = classifyBand(value, bands);
        if (band) cell.band = { colour: band.colour, label: band.label, marker: bandMarker(bands, band) };
      }
      cells[col.id] = cell;
    }
    return cells;
  };
  const rows: GridRow[] = sorted.map((r) => ({ id: r.id, label: r.label, status: r.status, isOther: r.isOther, cells: toCells(r) }));

  // 7. Averages: a simple mean of the project rows (the spreadsheet's rule), over the rows before any Other fold.
  let averages: GridRow | null = null;
  const averageValues: Record<string, number | null> = {};
  // A single project is its own average, so the row is only drawn for two or more.
  if (config.averageRow && base.length >= 2) {
    const avgRows: TransformRow[] = base.map((r) => ({ id: r.id, values: r.values }));
    const raw: RawRow = { id: "__average__", label: AVERAGE_ROW_LABEL, status: "", isOther: false, values: {}, texts: {}, noCost: new Set() };
    for (const col of columns) {
      if (col.kind === "month" || col.kind === "total") {
        raw.values[col.id] = average(avgRows, col.id);
        averageValues[col.id] = raw.values[col.id];
      }
    }
    const cells = toCells(raw);
    for (const col of columns) {
      if (col.kind === "derived") cells[col.id] = { text: "" };
    }
    averages = { id: raw.id, label: raw.label, status: "", isOther: false, cells };
  }

  // 8. Legend: one entry per rule whose measure is on screen and whose colours are allowed right now.
  const shownMeasures = new Set(columns.filter((c) => (c.kind === "month" || c.kind === "total") && !c.hidden).map((c) => c.measure));
  const legend: GridLegendEntry[] = [];
  for (const rule of config.formatRules) {
    const def: GridMeasureDef | undefined = gridMeasure(rule.measure);
    if (!def || !shownMeasures.has(rule.measure)) continue;
    if (safeMode && def.money) continue;
    legend.push({ measureLabel: def.label, bands: describeBands(rule.bands as FormatBand[], def.isPercent ? "%" : "") });
  }

  // 9. Notes the reader needs next to the figures.
  const notes: string[] = [];
  const affected = ["cost", "profit", "profitPercent"];
  if (months.length > 0 && config.measures.some((m) => affected.includes(m))) notes.push(RAW_WAGES_NOTE);
  if (config.summary.includes("category")) {
    const cfg = paretoConfig;
    const parts = cfg.bands.map((b) => `${b.label} up to ${b.upToPercent}%`).join(", ");
    notes.push(`Category: ${parts}, then ${cfg.restLabel}. A project is placed by the share of the total held by the projects above it, so the project that crosses a line stays in the band before it.`);
  }
  if (base.some((r) => r.noCost.size > 0)) {
    notes.push(
      input.showNoCostMonths === true
        ? `Months marked "${COSTS_NOT_ENTERED_LABEL}" have income but no costs yet, so their profit is overstated.`
        : `Project months with income but no costs entered yet are left out of the figures and of the Total. Costs go in around the 8th of the next month.`,
    );
  }
  notes.push("Average is the simple mean of the project rows. A month with no data shows a dash, not zero.");
  if (foldedCount > 0) notes.push(`Other adds together the ${foldedCount} projects below the top ${config.topN}.`);

  const numbers = new Map<string, Record<string, number | null>>();
  for (const r of sorted) numbers.set(r.id, r.values);
  if (averages) numbers.set(averages.id, averageValues);

  return {
    model: { columns, visibleColumns: columns.filter((c) => !c.hidden), rows, averages, legend, notes, foldedCount, sort },
    numbers,
  };
}

function sortRows(rows: RawRow[], sort: PnlGridSort, columns: GridColumn[]): RawRow[] {
  const column = columns.find((c) => c.id === sort.column);
  const sign = sort.direction === "asc" ? 1 : -1;
  const body = rows.filter((r) => !r.isOther);
  const other = rows.filter((r) => r.isOther);
  if (!column) return rows;
  const key = (r: RawRow): number | string | null => {
    if (column.id === NAME_COLUMN_ID) return r.label;
    if (column.unit === "text") return r.texts[column.id] ?? null;
    return r.values[column.id] ?? null;
  };
  body.sort((a, b) => {
    const av = key(a);
    const bv = key(b);
    if (av === null && bv === null) return a.label.localeCompare(b.label);
    if (av === null) return 1; // blanks last in either direction
    if (bv === null) return -1;
    if (typeof av === "string" && typeof bv === "string") return sign * av.localeCompare(bv) || a.label.localeCompare(b.label);
    return sign * ((av as number) - (bv as number)) || a.label.localeCompare(b.label);
  });
  return [...body, ...other];
}

/** The sort a header click moves to: first click on a new column starts descending for figures and ascending for text, a repeat flips it. */
export function nextSort(current: PnlGridSort, column: GridColumn): PnlGridSort {
  if (current.column === column.id) return { column: column.id, direction: current.direction === "asc" ? "desc" : "asc" };
  return { column: column.id, direction: column.unit === "text" ? "asc" : "desc" };
}

// Rows for CSV and XLSX: real numbers at the precision shown, text for names
// and categories, the averages row first. Covers exactly the columns on screen
// (the viewer's hidden columns stay out) and the same rows, Other included. The
// component never calls this under Safe Mode.
export function gridExportRecords(
  input: GridModelInput,
  exportNumber: (value: number | null | undefined, unit: PnlUnit) => number | string,
  exportHeader: (label: string, unit: PnlUnit | "text") => string,
): Record<string, string | number>[] {
  const { model, numbers } = computeGrid(input);
  const header = (c: GridColumn): string => {
    if (c.kind === "derived") return `${c.label} (${c.group})`;
    return c.group ? `${c.group} ${exportHeader(c.label, c.unit)}` : exportHeader(c.label, c.unit);
  };
  const all = model.averages ? [model.averages, ...model.rows] : model.rows;
  return all.map((row) => {
    const record: Record<string, string | number> = {};
    for (const c of model.visibleColumns) {
      if (c.id === NAME_COLUMN_ID) record[header(c)] = row.label;
      else if (c.unit === "text") record[header(c)] = row.cells[c.id]?.text ?? "";
      else if (row.cells[c.id]?.text === "") record[header(c)] = "";
      else record[header(c)] = exportNumber(numbers.get(row.id)?.[c.id] ?? null, c.unit);
    }
    return record;
  });
}

